import type { TableRecord } from '@powerportalspro/core';
import { RecordContext, useT } from '@powerportalspro/react';
import { AddressDisplayValue } from '@powerportalspro/react-fluent';

// Reuses the Blazor demo's localization keys (DisplayValuesAddressDemo) so both stacks share strings.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Viewers.DisplayValues.DisplayValuesAddressDemo';

const text = (value: string) => ({ $type: 14 as const, value });

// On a page the address reads a <RecordContext> that loads its record from Dataverse. This account
// is handed in through `loadedRecord` instead, so the sample doesn't depend on data. It carries no
// address1_composite, so both addresses are composed from their parts.
// showMap adds the map pin, which needs <MapsProvider> from @powerportalspro/react-maps around the app.
const accountId = '00000000-0000-0000-0000-00000000000c';
const account: TableRecord = {
  id: accountId,
  tableName: 'account',
  properties: {
    address1_line1: text('1 Microsoft Way'),
    address1_line2: text('Building 92'),
    address1_city: text('Redmond'),
    address1_stateorprovince: text('WA'),
    address1_postalcode: text('98052'),
    address1_country: text('United States'),
    address2_line1: text('70 Wilson Street'),
    address2_city: text('London'),
    address2_postalcode: text('EC2A 2DB'),
    address2_country: text('United Kingdom'),
    address1_latitude: { $type: 4, value: 47.6396 },
    address1_longitude: { $type: 4, value: -122.13 },
  },
  formattedValues: {},
  isDirty: false,
};

export function DisplayValuesAddressSample() {
  const t = useT();

  return (
    <RecordContext table="account" id={accountId} loadedRecord={account} warnOnUnsavedChanges={false}>
      <div style={{ display: 'flex', gap: 48 }}>
        {/* Domestic: the country matches homeCountry, so it is left off. Its map pin is placed at
            address1_latitude and address1_longitude. */}
        <AddressDisplayValue
          prefix="address1"
          label={t(`${STRINGS_BASE}.head-office-label`)}
          homeCountry="United States"
          showMap
        />

        {/* International: the country differs from homeCountry, so it is shown. It has no
            coordinates, so the map finds it with the provider's search. */}
        <AddressDisplayValue
          prefix="address2"
          label={t(`${STRINGS_BASE}.branch-office-label`)}
          homeCountry="United States"
          showMap
        />
      </div>
    </RecordContext>
  );
}
